import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Drawer,
  Empty,
  Icon,
  Input,
  Radio,
  Row,
  Select,
  Slider,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Timeline,
  message
} from 'antd';
import L from 'leaflet';
import { Circle, CircleMarker, Map as LeafletMap, Marker, Polyline, TileLayer, Tooltip as LeafletTooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import moment from 'moment';
import {
  fetchBeaconData,
  fetchRfidData,
  fetchRfidByStatus
} from './utils/passiveAssetService';
import {
  normalizeBeaconList,
  normalizeRfidList
} from './utils/passiveAssetNormalizers';
import {
  classifyPassiveAssetRead,
  buildRepeatIndex,
  CLASSIFICATION,
  CLASSIFICATION_LABEL,
  CLASSIFICATION_COLOR
} from './utils/passiveAssetClassification';
import {
  rssiToDistance,
  rssiConfidenceBand,
  CONFIDENCE_COLORS,
  DEFAULT_RSSI_OPTIONS
} from './utils/passiveAssetRssi';
import { loadAssetSensorIndex } from './utils/passiveAssetResolver';
import { useGeoFences } from './utils/geoFenceService';
import { ESRI_TILE_URL, ESRI_TILE_ATTRIBUTION, renderGeoFencePolygons } from './utils/passiveAssetUi';
import {
  buildDetectionIntervals,
  fetchReaderTrack,
  buildOccupancyClusters,
  summarizeReaderDetections,
  classifyIntervalTowing
} from './utils/passiveAssetMovement';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

const DEFAULT_RANGE = () => [moment().subtract(24, 'hours'), moment()];

const SENSOR_OPTIONS = [
  { value: 'ALL', label: 'All Sensors' },
  { value: 'RFID', label: 'RFID Only' },
  { value: 'BEACON', label: 'Beacon Only' }
];

const STATUS_OPTIONS = ['ACTIVE', 'INACTIVE', 'EXPIRED', 'PENDING'];

// Colors for distinct FOUND→LOST reader-track segments on the map.
const TRACK_COLORS = ['#1890ff', '#13c2c2', '#722ed1', '#fa8c16', '#eb2f96', '#2f54eb', '#a0d911', '#fa541c'];

// A jump between two consecutive plotted points is treated as an UNTRACKED move
// (e.g. the asset was towed by a non-GPS vehicle and re-detected elsewhere) when
// the points belong to different detection cycles, or the time gap exceeds this.
// Such segments are drawn dotted rather than solid.
const TRACK_JUMP_GAP_MS = 10 * 60 * 1000; // 10 minutes

const formatTime = (millis) => {
  if (!millis) return '-';
  const m = moment(Number(millis));
  return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : '-';
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const sensorTag = (sensorType) => {
  if (sensorType === 'RFID') return <Tag color="purple">RFID</Tag>;
  if (sensorType === 'BEACON') return <Tag color="cyan">Beacon</Tag>;
  return <Tag>{sensorType || '-'}</Tag>;
};

const classificationTag = (classification) => (
  <Tag color={CLASSIFICATION_COLOR[classification] || 'default'}>
    {CLASSIFICATION_LABEL[classification] || classification}
  </Tag>
);

const renderEmpty = (description) => (
  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={description} />
);

// Render a sensor identifier as the linked asset (code / alt name + owner),
// falling back to the raw id with an "unlinked" tag when no asset matches.
const renderAssetCell = (rawId, info) => {
  const hasRaw = rawId && rawId !== '-';
  if (info && (info.displayName || info.assetCode)) {
    return (
      <div>
        <div style={{ fontWeight: 600 }}>{info.displayName || info.assetCode}</div>
        <div style={{ fontSize: 11, color: '#8c8c8c' }}>
          {info.ownerCode ? `Owner ${info.ownerCode} · ` : ''}{hasRaw ? rawId : ''}
        </div>
        {info.viaDeviceCode && (
          <div style={{ fontSize: 11, color: '#bfbfbf' }}>via tag {info.viaDeviceCode}</div>
        )}
      </div>
    );
  }
  return (
    <div>
      <div>{hasRaw ? rawId : '-'}</div>
      {hasRaw && <Tag style={{ marginTop: 2 }}>unlinked</Tag>}
    </div>
  );
};

// Compact single-line asset label for tooltips / inline text.
const assetLabel = (rawId, info) => {
  if (info && (info.displayName || info.assetCode)) {
    const owner = info.ownerCode ? ` (${info.ownerCode})` : '';
    return `${info.displayName || info.assetCode}${owner}`;
  }
  return rawId || '-';
};

const showApiError = (err, fallback) => {
  const msg = (err && err.message) || fallback || 'API request failed';
  message.error(msg);
};

const responsePayload = (res) => {
  if (!res) return null;
  if (res.success === false) return null;
  return res.data === undefined ? res : res.data;
};

function PassiveAssetVisibility() {
  // ---------- Global filters ----------
  const [range, setRange] = useState(DEFAULT_RANGE());
  const [sensorFilter, setSensorFilter] = useState('ALL');
  const [rfidInput, setRfidInput] = useState('');
  const [macInput, setMacInput] = useState('');
  const [imeiInput, setImeiInput] = useState('');
  const [statusInput, setStatusInput] = useState(undefined);

  // ---------- Data ----------
  const [rfidReads, setRfidReads] = useState([]);
  const [beaconReads, setBeaconReads] = useState([]);
  const [loadingRfid, setLoadingRfid] = useState(false);
  const [loadingBeacon, setLoadingBeacon] = useState(false);
  const [rfidError, setRfidError] = useState(null);
  const [beaconError, setBeaconError] = useState(null);
  const [rfidDebug, setRfidDebug] = useState(null);
  const [beaconDebug, setBeaconDebug] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [hideEmptyRfid, setHideEmptyRfid] = useState(true);

  // ---------- Asset enrichment (sensor id -> asset code/owner/alt name) ----------
  const [assetIndex, setAssetIndex] = useState(null);
  const [assetIndexLoading, setAssetIndexLoading] = useState(false);
  const [assetIndexError, setAssetIndexError] = useState(null);
  const { geoFences } = useGeoFences();

  // ---------- Asset / Reader Timeline tab state ----------
  const [timelineSensor, setTimelineSensor] = useState('RFID');
  const [timelineIdInput, setTimelineIdInput] = useState('');
  const [timelineReads, setTimelineReads] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const [readerSensor, setReaderSensor] = useState('RFID');
  const [readerImeiInput, setReaderImeiInput] = useState('');
  const [readerReads, setReaderReads] = useState([]);
  const [readerLoading, setReaderLoading] = useState(false);

  // ---------- Drawer ----------
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRecord, setDrawerRecord] = useState(null);

  // ---------- Map / playback ----------
  const [mapSensor, setMapSensor] = useState('BEACON');
  const [playbackIndex, setPlaybackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const mapRef = useRef(null);
  const playbackTimerRef = useRef(null);

  // RSSI → distance tuning (path-loss model parameters)
  const [measuredPower, setMeasuredPower] = useState(DEFAULT_RSSI_OPTIONS.measuredPower);
  const [environmentN, setEnvironmentN] = useState(DEFAULT_RSSI_OPTIONS.environmentN);
  const [signedAsNegative, setSignedAsNegative] = useState(DEFAULT_RSSI_OPTIONS.signedAsNegative);

  // ---------- Reader-scan clustering (one reader, many BLE assets) ----------
  const [clusterByScan, setClusterByScan] = useState(false);

  // ---------- Reader GPS track (FOUND -> LOST movement) ----------
  const [showReaderTrack, setShowReaderTrack] = useState(false);
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackError, setTrackError] = useState(null);
  const [trackNotice, setTrackNotice] = useState(null);
  const [readerTracks, setReaderTracks] = useState([]); // [{key, assetIdentifier, readerImei, startTs, endTs, open, unlinked, points, error}]
  const trackCacheRef = useRef(new Map());
  const trackRunRef = useRef(0);
  const MAX_TRACK_INTERVALS = 25;

  // Map data side panel (mirrors the rows plotted on the map).
  const [dataPanelOpen, setDataPanelOpen] = useState(false);
  const dataPanelBodyRef = useRef(null);

  // ---------- Derived ----------
  const allReads = useMemo(() => [...rfidReads, ...beaconReads], [rfidReads, beaconReads]);
  const repeatIndex = useMemo(() => buildRepeatIndex(allReads), [allReads]);

  // A read "found no tag" when its asset identifier is missing/placeholder.
  const hasTag = (r) => r && r.assetIdentifier && r.assetIdentifier !== '-';

  const displayedRfidReads = useMemo(
    () => (hideEmptyRfid ? rfidReads.filter(hasTag) : rfidReads),
    [rfidReads, hideEmptyRfid]
  );
  const emptyRfidCount = useMemo(
    () => rfidReads.filter((r) => !hasTag(r)).length,
    [rfidReads]
  );

  // Load the asset index once on mount so we can resolve sensor ids to assets.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setAssetIndexLoading(true);
      setAssetIndexError(null);
      try {
        const idx = await loadAssetSensorIndex();
        if (!cancelled) setAssetIndex(idx);
      } catch (err) {
        if (!cancelled) setAssetIndexError(err.message || 'Failed to load asset list');
      } finally {
        if (!cancelled) setAssetIndexLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const resolveTag = useCallback(
    (id) => (assetIndex ? assetIndex.resolveTag(id) : null),
    [assetIndex]
  );
  const resolveReader = useCallback(
    (id) => (assetIndex ? assetIndex.resolveReader(id) : null),
    [assetIndex]
  );

  const fromMs = range && range[0] ? range[0].valueOf() : null;
  const toMs = range && range[1] ? range[1].valueOf() : null;

  // ---------- Map playback points (only reads with valid lat/lng) ----------
  const mapPoints = useMemo(() => {
    let source;
    if (mapSensor === 'RFID') source = rfidReads;
    else if (mapSensor === 'BEACON') source = beaconReads;
    else source = allReads;

    return source
      .filter((r) =>
        r &&
        r.latitude !== null && r.latitude !== undefined &&
        r.longitude !== null && r.longitude !== undefined &&
        Number.isFinite(Number(r.latitude)) && Number.isFinite(Number(r.longitude)) &&
        r.eventTime
      )
      .map((r) => {
        const distance = rssiToDistance(r.rssi, { measuredPower, environmentN, signedAsNegative });
        return {
          id: r.id,
          ts: Number(r.eventTime),
          lat: Number(r.latitude),
          lng: Number(r.longitude),
          readerImei: r.readerImei,
          assetIdentifier: r.assetIdentifier,
          rssi: r.rssi,
          zone: r.zone,
          sensorType: r.sensorType,
          distance,
          confidence: rssiConfidenceBand(distance),
          raw: r
        };
      })
      .sort((a, b) => a.ts - b.ts);
  }, [mapSensor, rfidReads, beaconReads, allReads, measuredPower, environmentN, signedAsNegative]);

  // ---------- Reader GPS track fetch (FOUND -> LOST movement) ----------
  useEffect(() => {
    if (!showReaderTrack) {
      setReaderTracks([]);
      setTrackError(null);
      setTrackNotice(null);
      setTrackLoading(false);
      return;
    }
    if (!beaconReads || beaconReads.length === 0) {
      setReaderTracks([]);
      setTrackNotice('Fetch beacon reads first to plot reader GPS movement.');
      return;
    }

    const { intervals } = buildDetectionIntervals(beaconReads, toMs);
    if (intervals.length === 0) {
      setReaderTracks([]);
      setTrackNotice('No FOUND/LOST events in the loaded beacon reads, so no movement windows to plot.');
      return;
    }

    // Group intervals by READER so we pull each reader's GPS path once and show
    // every asset that rode with it (a reader can tow multiple BLE assets).
    const byReader = new Map();
    intervals.forEach((itv) => {
      if (!byReader.has(itv.readerImei)) {
        byReader.set(itv.readerImei, { readerImei: itv.readerImei, intervals: [], spanStart: itv.startTs, spanEnd: itv.endTs });
      }
      const g = byReader.get(itv.readerImei);
      g.intervals.push(itv);
      g.spanStart = Math.min(g.spanStart, itv.startTs);
      g.spanEnd = Math.max(g.spanEnd, itv.endTs);
    });

    let readers = Array.from(byReader.values()).sort((a, b) => a.spanStart - b.spanStart);
    let capped = false;
    if (readers.length > MAX_TRACK_INTERVALS) {
      readers = readers.slice(0, MAX_TRACK_INTERVALS);
      capped = true;
    }

    const runId = trackRunRef.current + 1;
    trackRunRef.current = runId;
    setTrackLoading(true);
    setTrackError(null);
    setTrackNotice(null);

    // When the data is for a single reader (typical "query by IMEI"), pull the
    // reader's GPS route for the WHOLE selected period so we also see what the
    // tug did while carrying nothing. Otherwise bound cost to detection spans.
    const singleReader = readers.length === 1;

    const run = async () => {
      const results = [];
      let unlinkedCount = 0;
      let errorCount = 0;

      for (const rd of readers) {
        const readerInfo = resolveReader(rd.readerImei);
        const assetId = readerInfo ? readerInfo.assetId : null;
        const anyOpen = rd.intervals.some((i) => i.open);
        const spanStart = singleReader ? fromMs : rd.spanStart;
        const spanEnd = singleReader ? toMs : rd.spanEnd;

        if (!assetId) {
          unlinkedCount += 1;
          results.push({ key: rd.readerImei, readerImei: rd.readerImei, intervals: rd.intervals, spanStart, spanEnd, open: anyOpen, unlinked: true, points: [], error: 'Reader not linked to an asset' });
          continue;
        }

        const cacheKey = `${rd.readerImei}|${assetId}|${spanStart}|${spanEnd}`;
        let entry = trackCacheRef.current.get(cacheKey);
        if (!entry) {
          // eslint-disable-next-line no-await-in-loop
          const res = await fetchReaderTrack({
            readerImei: rd.readerImei,
            assetId,
            startTs: spanStart,
            endTs: spanEnd
          });
          entry = { points: res.points || [], error: res.success ? null : res.error };
          trackCacheRef.current.set(cacheKey, entry);
        }
        if (entry.error) errorCount += 1;
        // Classify each asset's interval as towed (moved with reader) vs just detected.
        const classifiedIntervals = rd.intervals.map((itv) => {
          const c = classifyIntervalTowing(itv, entry.points);
          return { ...itv, towed: c.towed, towDistanceM: c.distanceM, towDurationMs: c.durationMs };
        });
        results.push({ key: rd.readerImei, readerImei: rd.readerImei, intervals: classifiedIntervals, spanStart, spanEnd, open: anyOpen, unlinked: false, points: entry.points, error: entry.error });
      }

      if (trackRunRef.current !== runId) return; // stale run, ignore

      setReaderTracks(results);
      setTrackLoading(false);
      const notes = [];
      if (capped) notes.push(`Showing first ${MAX_TRACK_INTERVALS} of ${byReader.size} readers.`);
      if (unlinkedCount > 0) notes.push(`${unlinkedCount} reader(s) unlinked (straight FOUND→LOST line shown).`);
      if (errorCount > 0) notes.push(`${errorCount} reader(s) returned no GPS data.`);
      setTrackNotice(notes.length ? notes.join(' ') : null);
    };

    run().catch((err) => {
      if (trackRunRef.current !== runId) return;
      setTrackLoading(false);
      setTrackError(err.message || 'Failed to load reader GPS tracks');
    });
  }, [showReaderTrack, beaconReads, fromMs, toMs, resolveReader]);

  // GPS track points for playback. We order points PER reader segment (then by
  // time within the segment) and never globally interleave different readers —
  // interleaving distant readers would create nonsense "jumps". In asset view
  // (a single tag) we only plot where the tag was actually TOWED, so a stationary
  // tag detected at range by a roaming reader doesn't flood the map.
  const trackPoints = useMemo(() => {
    if (!showReaderTrack) return [];

    // Asset view = the loaded data is about a single tag (seen by ≥1 readers).
    const assetSet = new Set();
    readerTracks.forEach((t) => (t.intervals || []).forEach((i) => assetSet.add(i.assetIdentifier)));
    const assetView = assetSet.size <= 1;

    const ordered = readerTracks
      .map((t, ti) => ({ t, ti }))
      .sort((a, b) => (a.t.spanStart || 0) - (b.t.spanStart || 0));

    const pts = [];
    ordered.forEach(({ t, ti }) => {
      const segId = `${t.key}|${ti}`;
      const itvs = t.intervals || [];
      const pointArr = t.points || [];
      pointArr.forEach((p, i) => {
        // On-board manifest: every asset whose FOUND→LOST interval is active now.
        const detections = itvs
          .filter((itv) => itv.startTs <= p.ts && p.ts <= itv.endTs)
          .map((itv) => ({
            assetIdentifier: itv.assetIdentifier,
            rssi: itv.startRead ? itv.startRead.rssi : null,
            status: itv.towed ? 'TOWED' : 'DETECTED',
            towed: itv.towed === true,
            open: itv.open
          }))
          .sort((a, b) => (Number(b.towed) - Number(a.towed)) || (Number(b.rssi) - Number(a.rssi)));
        const towedCount = detections.filter((d) => d.towed).length;

        // In asset view, only keep points where the tag was being towed.
        if (assetView && towedCount === 0) return;

        pts.push({
          id: `${t.key}-${p.id || i}`,
          ts: p.ts,
          lat: p.lat,
          lng: p.lng,
          speed: p.speed,
          noGps: p.noGps === true,
          readerImei: t.readerImei,
          assetIdentifier: detections.length === 1 ? detections[0].assetIdentifier : null,
          detections,
          count: detections.length,
          towedCount,
          intervalKey: t.key,
          segId,
          cycleIndex: ti,
          open: t.open,
          raw: p.raw
        });
      });
    });

    // Points are already in segment+time order; mark jumps at segment boundaries
    // or long time gaps (these render dotted).
    for (let i = 0; i < pts.length; i += 1) {
      const prev = pts[i - 1];
      pts[i].jumpFromPrev = Boolean(
        prev && (prev.segId !== pts[i].segId || (pts[i].ts - prev.ts) > TRACK_JUMP_GAP_MS)
      );
    }
    return pts;
  }, [showReaderTrack, readerTracks]);

  // ---------- BLE asset journey (dwells + towed moves) ----------
  // For a single tag: a stationary tag detected by a roaming reader produces
  // scattered reader positions, not movement. So we collapse each non-towed
  // FOUND→LOST interval into a single DWELL (the tag's best-estimate location =
  // strongest-signal detection), and render each TOWED interval as a MOVE drawn
  // from the towing reader's actual GPS between dwells.
  const assetJourney = useMemo(() => {
    if (!showReaderTrack || readerTracks.length === 0) return [];
    const assetSet = new Set();
    readerTracks.forEach((t) => (t.intervals || []).forEach((i) => assetSet.add(i.assetIdentifier)));
    if (assetSet.size > 1) return []; // reader/mixed view handled by trackPoints

    const legs = [];
    readerTracks.forEach((t) => {
      (t.intervals || []).forEach((itv) => {
        const segPts = (t.points || []).filter((p) => p.ts >= itv.startTs && p.ts <= itv.endTs);
        if (itv.towed && segPts.length >= 2) {
          legs.push({ type: 'move', startTs: itv.startTs, endTs: itv.endTs, readerImei: t.readerImei, assetIdentifier: itv.assetIdentifier, points: segPts });
        } else {
          // Dwell location = strongest detection in the window (closest approach).
          const reads = beaconReads.filter((r) =>
            r.assetIdentifier === itv.assetIdentifier &&
            r.readerImei === t.readerImei &&
            r.eventTime >= itv.startTs && r.eventTime <= itv.endTs &&
            Number.isFinite(Number(r.latitude)) && Number.isFinite(Number(r.longitude)) &&
            !(Math.abs(Number(r.latitude)) < 1e-6 && Math.abs(Number(r.longitude)) < 1e-6));
          let best = null;
          reads.forEach((r) => { if (!best || Number(r.rssi) > Number(best.rssi)) best = r; });
          const loc = best || itv.startRead;
          if (loc && loc.latitude != null && Number.isFinite(Number(loc.latitude))) {
            legs.push({ type: 'dwell', startTs: itv.startTs, endTs: itv.endTs, readerImei: t.readerImei, assetIdentifier: itv.assetIdentifier, lat: Number(loc.latitude), lng: Number(loc.longitude), rssi: loc.rssi, count: reads.length });
          }
        }
      });
    });
    legs.sort((a, b) => a.startTs - b.startTs);

    const pts = [];
    legs.forEach((leg, li) => {
      const segId = `leg-${li}`;
      if (leg.type === 'move') {
        leg.points.forEach((p) => pts.push({
          id: `${segId}-${p.id || p.ts}`,
          ts: p.ts, lat: p.lat, lng: p.lng, speed: p.speed, noGps: p.noGps === true,
          readerImei: leg.readerImei, assetIdentifier: leg.assetIdentifier,
          detections: [{ assetIdentifier: leg.assetIdentifier, rssi: null, status: 'TOWED', towed: true }],
          count: 1, towedCount: 1, dwell: false, segId
        }));
      } else {
        pts.push({
          id: `${segId}-dwell`,
          ts: leg.startTs, lat: leg.lat, lng: leg.lng,
          readerImei: leg.readerImei, assetIdentifier: leg.assetIdentifier,
          detections: [{ assetIdentifier: leg.assetIdentifier, rssi: leg.rssi, status: 'DETECTED', towed: false }],
          count: 1, towedCount: 0, dwell: true, dwellStartTs: leg.startTs, dwellEndTs: leg.endTs, dwellCount: leg.count, segId
        });
      }
    });
    for (let i = 0; i < pts.length; i += 1) {
      pts[i].jumpFromPrev = Boolean(pts[i - 1] && pts[i - 1].segId !== pts[i].segId);
    }
    return pts;
  }, [showReaderTrack, readerTracks, beaconReads]);

  // Reader-scan clusters: every BLE asset detected by a reader at the same scan
  // time, clubbed into one map point.
  const scanClusters = useMemo(() => {
    if (!clusterByScan || mapSensor === 'RFID') return [];
    return buildOccupancyClusters(beaconReads, toMs);
  }, [clusterByScan, mapSensor, beaconReads, toMs]);

  const clusterPoints = useMemo(
    () => scanClusters.map((c) => ({
      id: c.key,
      ts: c.ts,
      lat: c.lat,
      lng: c.lng,
      readerImei: c.readerImei,
      assetIdentifier: null,
      detections: c.detections,
      count: c.count
    })),
    [scanClusters]
  );

  // Per-asset towed/pass-by summary for the loaded reader data.
  const detectionSummary = useMemo(() => {
    if (!clusterByScan || beaconReads.length === 0) return null;
    return summarizeReaderDetections(beaconReads, toMs);
  }, [clusterByScan, beaconReads, toMs]);

  // Perspective of the loaded data: one tag seen by many readers (asset view) vs
  // one reader carrying many tags (reader/tug view). The detection logic is the
  // same for BLE and RFID; only the lens differs.
  const perspective = useMemo(() => {
    const source = mapSensor === 'RFID' ? rfidReads : mapSensor === 'BEACON' ? beaconReads : allReads;
    const assets = new Set();
    const readers = new Set();
    source.forEach((r) => {
      if (r.assetIdentifier && r.assetIdentifier !== '-') assets.add(r.assetIdentifier);
      if (r.readerImei && r.readerImei !== '-') readers.add(r.readerImei);
    });
    const a = assets.size;
    const rd = readers.size;
    if (a === 0 && rd === 0) return { mode: 'none', assets: a, readers: rd };
    if (rd <= 1 && a > 1) return { mode: 'reader', assets: a, readers: rd };
    if (a <= 1 && rd >= 1) return { mode: 'asset', assets: a, readers: rd };
    if (a > 1 && rd > 1) return { mode: 'mixed', assets: a, readers: rd };
    return { mode: 'single', assets: a, readers: rd };
  }, [mapSensor, rfidReads, beaconReads, allReads]);

  // Whichever point set drives the map + playback right now: clusters →
  // BLE asset journey (dwells + moves) → reader GPS track → raw reads.
  const usingClusters = clusterByScan && clusterPoints.length > 0;
  const usingJourney = !usingClusters && showReaderTrack && assetJourney.length > 0;
  const usingTrack = !usingClusters && !usingJourney && showReaderTrack && trackPoints.length > 0;
  const activeMapPoints = usingClusters
    ? clusterPoints
    : usingJourney
      ? assetJourney
      : (usingTrack ? trackPoints : mapPoints);

  const activePoint = useMemo(() => {
    if (activeMapPoints.length === 0) return null;
    const idx = Math.max(0, Math.min(playbackIndex, activeMapPoints.length - 1));
    return activeMapPoints[idx];
  }, [activeMapPoints, playbackIndex]);

  // Base track split into continuous solid runs and dotted "untracked jump"
  // connectors (e.g. the asset towed by a non-GPS vehicle between detection
  // windows). Runs carry their cycle index for coloring.
  const baseTrack = useMemo(() => {
    if (!usingTrack) return { runs: [], jumps: [] };
    const runs = [];
    const jumps = [];
    let run = null;
    for (let i = 0; i < trackPoints.length; i += 1) {
      const p = trackPoints[i];
      if (i > 0 && p.jumpFromPrev) {
        if (run && run.positions.length >= 2) runs.push(run);
        const a = trackPoints[i - 1];
        jumps.push({ positions: [[a.lat, a.lng], [p.lat, p.lng]], gapMs: p.ts - a.ts });
        run = { positions: [[p.lat, p.lng]], cycleIndex: p.cycleIndex };
      } else {
        if (!run) run = { positions: [], cycleIndex: p.cycleIndex };
        run.positions.push([p.lat, p.lng]);
      }
    }
    if (run && run.positions.length >= 2) runs.push(run);
    return { runs, jumps };
  }, [usingTrack, trackPoints]);

  // Route stretches classified by state: 'towing' (≥1 asset towed), 'detected'
  // (assets present but none towed), else empty (not drawn). Breaks at jumps and
  // at state transitions so each run is a single color.
  const routeSegments = useMemo(() => {
    if (!usingTrack) return [];
    // Each edge (i-1 → i) is colored by the load while travelling it (state at i-1).
    const edgeState = (p) => ((p.towedCount || 0) > 0 ? 'towing' : (p.count || 0) > 0 ? 'detected' : 'empty');
    const segs = [];
    let run = null;
    for (let i = 1; i < trackPoints.length; i += 1) {
      const a = trackPoints[i - 1];
      const b = trackPoints[i];
      if (b.jumpFromPrev) { if (run) { segs.push(run); run = null; } continue; }
      const st = edgeState(a);
      if (st === 'empty') { if (run) { segs.push(run); run = null; } continue; }
      if (run && run.state === st) {
        run.positions.push([b.lat, b.lng]);
      } else {
        if (run) segs.push(run);
        run = { state: st, positions: [[a.lat, a.lng], [b.lat, b.lng]] };
      }
    }
    if (run) segs.push(run);
    return segs;
  }, [usingTrack, trackPoints]);

  // BLE asset journey render data: move polylines, dwell circles, and dotted
  // connectors for transitions between legs.
  const journeyMoveSegments = useMemo(() => {
    if (!usingJourney) return [];
    const segs = [];
    let run = null;
    for (let i = 0; i < assetJourney.length; i += 1) {
      const p = assetJourney[i];
      if (p.dwell) { if (run) { segs.push(run); run = null; } continue; }
      if (i > 0 && p.jumpFromPrev) { if (run) { segs.push(run); run = null; } }
      if (!run) run = { positions: [] };
      run.positions.push([p.lat, p.lng]);
    }
    if (run) segs.push(run);
    return segs.filter((s) => s.positions.length >= 2);
  }, [usingJourney, assetJourney]);

  const journeyDwells = useMemo(
    () => (usingJourney ? assetJourney.filter((p) => p.dwell) : []),
    [usingJourney, assetJourney]
  );

  const journeyJumps = useMemo(() => {
    if (!usingJourney) return [];
    const out = [];
    for (let i = 1; i < assetJourney.length; i += 1) {
      if (assetJourney[i].jumpFromPrev) {
        const a = assetJourney[i - 1];
        const b = assetJourney[i];
        out.push([[a.lat, a.lng], [b.lat, b.lng]]);
      }
    }
    return out;
  }, [usingJourney, assetJourney]);

  // Split the played-so-far portion into solid runs (continuous track) and
  // dotted jump connectors, so playback never draws a solid line across a tow.
  const playedTrack = useMemo(() => {
    if (!usingTrack && !usingJourney) return { runs: [], jumps: [] };
    const upto = Math.max(0, Math.min(playbackIndex, activeMapPoints.length - 1));
    const runs = [];
    const jumps = [];
    let run = [];
    for (let i = 0; i <= upto; i += 1) {
      const p = activeMapPoints[i];
      if (i > 0 && p.jumpFromPrev) {
        if (run.length >= 2) runs.push(run);
        const a = activeMapPoints[i - 1];
        jumps.push([[a.lat, a.lng], [p.lat, p.lng]]);
        run = [[p.lat, p.lng]];
      } else {
        run.push([p.lat, p.lng]);
      }
    }
    if (run.length >= 2) runs.push(run);
    return { runs, jumps };
  }, [usingTrack, activeMapPoints, playbackIndex]);

  // Rows backing the map, for the side data panel (keyed by index so we can
  // highlight + scroll to the active playback row).
  const panelData = useMemo(
    () => activeMapPoints.map((p, i) => ({ ...p, _idx: i, key: i })),
    [activeMapPoints]
  );

  // Keep the active row centered in the data panel as playback advances.
  useEffect(() => {
    if (!dataPanelOpen) return;
    const root = dataPanelBodyRef.current;
    if (!root) return;
    const row = root.querySelector(`[data-row-key="${playbackIndex}"]`);
    if (!row) return;
    // AntD Table's scroll={{y}} puts the scrollable viewport in .ant-table-body.
    const body = root.querySelector('.ant-table-body');
    if (body) {
      const bodyRect = body.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      const delta = (rowRect.top - bodyRect.top) - (body.clientHeight / 2 - rowRect.height / 2);
      body.scrollTop += delta;
    } else if (typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'center' });
    }
  }, [playbackIndex, dataPanelOpen, panelData.length]);

  // Reset playback whenever the underlying data set changes.
  useEffect(() => {
    setPlaybackIndex(0);
    setIsPlaying(false);
  }, [activeMapPoints.length, mapSensor, usingTrack]);

  // Drive the playback animation. Each "step" advances by 1 point; speed scales
  // the tick interval (base 600ms at 1x).
  useEffect(() => {
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
    if (!isPlaying) return undefined;
    if (activeMapPoints.length <= 1) {
      setIsPlaying(false);
      return undefined;
    }
    const baseMs = 600;
    const speed = Math.max(0.25, Number(playbackSpeed) || 1);
    const tick = Math.max(60, Math.round(baseMs / speed));

    playbackTimerRef.current = setInterval(() => {
      setPlaybackIndex((idx) => {
        if (idx >= activeMapPoints.length - 1) {
          setIsPlaying(false);
          return idx;
        }
        return idx + 1;
      });
    }, tick);

    return () => {
      if (playbackTimerRef.current) {
        clearInterval(playbackTimerRef.current);
        playbackTimerRef.current = null;
      }
    };
  }, [isPlaying, playbackSpeed, activeMapPoints.length]);

  // Fit map to all available points when the data set changes.
  useEffect(() => {
    if (!mapRef.current || activeMapPoints.length === 0) return;
    const mapInstance = mapRef.current.leafletElement || mapRef.current;
    if (!mapInstance || typeof mapInstance.fitBounds !== 'function') return;
    if (activeMapPoints.length === 1) {
      mapInstance.setView([activeMapPoints[0].lat, activeMapPoints[0].lng], 17, { animate: true });
      return;
    }
    const bounds = L.latLngBounds(activeMapPoints.map((p) => [p.lat, p.lng]));
    mapInstance.fitBounds(bounds, { padding: [32, 32], maxZoom: 18, animate: true });
  }, [activeMapPoints]);

  // Pan to current point while playing.
  useEffect(() => {
    if (!isPlaying || !activePoint || !mapRef.current) return;
    const mapInstance = mapRef.current.leafletElement || mapRef.current;
    if (!mapInstance || typeof mapInstance.panTo !== 'function') return;
    mapInstance.panTo([activePoint.lat, activePoint.lng], { animate: true });
  }, [isPlaying, activePoint]);

  const playbackPinIcon = useMemo(() => L.divIcon({
    className: 'passive-playback-pin',
    html: `
      <div style="position:relative;width:24px;height:24px;">
        <div style="position:absolute;left:1px;top:1px;width:22px;height:22px;background:#1890ff;border:2px solid #ffffff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 1px 4px rgba(0,0,0,0.4);"></div>
        <div style="position:absolute;left:8px;top:4px;width:10px;height:10px;color:#ffffff;font-weight:700;font-size:11px;line-height:10px;text-align:center;">&#9737;</div>
      </div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 24]
  }), []);

  // Greyed pin with a warning glyph for "no GPS fix — last known position".
  const noGpsPinIcon = useMemo(() => L.divIcon({
    className: 'passive-playback-pin-nogps',
    html: `
      <div style="position:relative;width:24px;height:24px;">
        <div style="position:absolute;left:1px;top:1px;width:22px;height:22px;background:#8c8c8c;border:2px dashed #ffffff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 1px 4px rgba(0,0,0,0.4);"></div>
        <div style="position:absolute;left:7px;top:3px;width:12px;height:12px;color:#ffffff;font-weight:700;font-size:12px;line-height:12px;text-align:center;">&#9888;</div>
      </div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 24]
  }), []);

  // ---------- KPI metrics (Overview) ----------
  const kpis = useMemo(() => {
    const now = Date.now();
    const oneHourAgo = now - 60 * 60 * 1000;
    const oneDayAgo = now - 24 * 60 * 60 * 1000;

    let seen1h = 0;
    let seen24h = 0;
    let weak = 0;
    let needsReview = 0;

    const uniqueTags = new Set();
    const uniqueMacs = new Set();
    const uniqueReaders = new Set();

    allReads.forEach((r) => {
      if (r.sensorType === 'RFID' && r.assetIdentifier && r.assetIdentifier !== '-') {
        uniqueTags.add(r.assetIdentifier);
      }
      if (r.sensorType === 'BEACON' && r.assetIdentifier && r.assetIdentifier !== '-') {
        uniqueMacs.add(r.assetIdentifier);
      }
      if (r.readerImei && r.readerImei !== '-') {
        uniqueReaders.add(r.readerImei);
      }
      if (r.eventTime && r.eventTime >= oneHourAgo) seen1h += 1;
      if (r.eventTime && r.eventTime >= oneDayAgo) seen24h += 1;
      if (r.rssi !== null && r.rssi !== undefined && Number(r.rssi) < -85) weak += 1;

      const c = classifyPassiveAssetRead(r, {
        repeatedDetectionCount: repeatIndex.get(r.assetIdentifier) || 0
      });
      if (c.classification === CLASSIFICATION.NEEDS_REVIEW || c.classification === CLASSIFICATION.POSSIBLE_WRONG_ZONE) {
        needsReview += 1;
      }
    });

    return {
      totalReads: allReads.length,
      uniqueTags: uniqueTags.size,
      uniqueMacs: uniqueMacs.size,
      uniqueReaders: uniqueReaders.size,
      seen1h,
      seen24h,
      weak,
      needsReview
    };
  }, [allReads, repeatIndex]);

  // ---------- Fetch handlers ----------
  const ensureRange = useCallback(() => {
    if (!fromMs || !toMs) {
      message.warning('Please select a valid date range.');
      return false;
    }
    if (fromMs >= toMs) {
      message.warning('Start time must be earlier than end time.');
      return false;
    }
    return true;
  }, [fromMs, toMs]);

  const loadRfid = useCallback(async () => {
    if (!ensureRange()) return;
    const hasRfid = Boolean(rfidInput && rfidInput.trim());
    const hasImei = Boolean(imeiInput && imeiInput.trim());
    const hasStatus = Boolean(statusInput);

    if (!hasRfid && !hasImei && !hasStatus) {
      setRfidError('Enter RFID, Reader IMEI, or pick a Status to fetch RFID data.');
      setRfidReads([]);
      return;
    }

    setLoadingRfid(true);
    setRfidError(null);

    // Reconstruct the endpoint we are about to hit (for the debug panel).
    let endpoint;
    if (!hasRfid && !hasImei && hasStatus) {
      endpoint = `/iot-device/rfid-data/status/${encodeURIComponent(statusInput)}/${fromMs}/${toMs}`;
    } else {
      const qp = [`from=${fromMs}`, `to=${toMs}`];
      if (hasRfid) qp.push(`rfid=${encodeURIComponent(rfidInput.trim())}`);
      if (hasImei) qp.push(`imei=${encodeURIComponent(imeiInput.trim())}`);
      if (hasStatus) qp.push(`status=${encodeURIComponent(statusInput)}`);
      endpoint = `/iot-device/rfid-data?${qp.join('&')}`;
    }

    try {
      let res;
      if (!hasRfid && !hasImei && hasStatus) {
        res = await fetchRfidByStatus({ status: statusInput, from: fromMs, to: toMs });
      } else {
        res = await fetchRfidData({
          from: fromMs,
          to: toMs,
          rfid: hasRfid ? rfidInput.trim() : undefined,
          imei: hasImei ? imeiInput.trim() : undefined,
          status: hasStatus ? statusInput : undefined
        });
      }

      if (res && res.success === false) {
        const errMsg = res.error || 'Failed to fetch RFID data';
        setRfidError(errMsg);
        setRfidReads([]);
        setRfidDebug({ endpoint, ok: false, error: errMsg, raw: res.error });
      } else {
        const payload = responsePayload(res);
        const normalized = normalizeRfidList(payload);
        setRfidReads(normalized);
        setRfidDebug({ endpoint, ok: true, extracted: normalized.length, raw: payload });
        if (normalized.length === 0) {
          message.info('No RFID reads found for the selected filters.');
        }
      }
    } catch (err) {
      setRfidError(err.message || 'Failed to fetch RFID data');
      setRfidReads([]);
      setRfidDebug({ endpoint, ok: false, error: err.message });
      showApiError(err, 'Failed to fetch RFID data');
    } finally {
      setLoadingRfid(false);
    }
  }, [ensureRange, rfidInput, imeiInput, statusInput, fromMs, toMs]);

  const loadBeacon = useCallback(async () => {
    if (!ensureRange()) return;
    const hasMac = Boolean(macInput && macInput.trim());
    const hasImei = Boolean(imeiInput && imeiInput.trim());

    if (!hasMac && !hasImei) {
      setBeaconError('Enter Beacon MAC or Device IMEI to fetch beacon data.');
      setBeaconReads([]);
      return;
    }

    setLoadingBeacon(true);
    setBeaconError(null);

    // mac wins over imei per the API guide.
    const beaconQp = [`from=${fromMs}`, `to=${toMs}`];
    if (hasMac) beaconQp.push(`mac=${encodeURIComponent(macInput.trim())}`);
    else if (hasImei) beaconQp.push(`imei=${encodeURIComponent(imeiInput.trim())}`);
    const endpoint = `/iot-device/beacon-data?${beaconQp.join('&')}`;

    try {
      const res = await fetchBeaconData({
        from: fromMs,
        to: toMs,
        mac: hasMac ? macInput.trim() : undefined,
        imei: hasImei ? imeiInput.trim() : undefined
      });

      if (res && res.success === false) {
        const errMsg = res.error || 'Failed to fetch beacon data';
        setBeaconError(errMsg);
        setBeaconReads([]);
        setBeaconDebug({ endpoint, ok: false, error: errMsg });
      } else {
        const payload = responsePayload(res);
        const normalized = normalizeBeaconList(payload);
        setBeaconReads(normalized);
        setBeaconDebug({ endpoint, ok: true, extracted: normalized.length, raw: payload });
        if (normalized.length === 0) {
          message.info('No beacon reads found for the selected filters.');
        }
      }
    } catch (err) {
      setBeaconError(err.message || 'Failed to fetch beacon data');
      setBeaconReads([]);
      setBeaconDebug({ endpoint, ok: false, error: err.message });
      showApiError(err, 'Failed to fetch beacon data');
    } finally {
      setLoadingBeacon(false);
    }
  }, [ensureRange, macInput, imeiInput, fromMs, toMs]);

  const loadAll = useCallback(() => {
    if (sensorFilter === 'RFID') {
      loadRfid();
      setBeaconReads([]);
      return;
    }
    if (sensorFilter === 'BEACON') {
      loadBeacon();
      setRfidReads([]);
      return;
    }
    // ALL: only fetch streams that have enough inputs to be valid
    const canRfid = (rfidInput && rfidInput.trim()) || (imeiInput && imeiInput.trim()) || statusInput;
    const canBeacon = (macInput && macInput.trim()) || (imeiInput && imeiInput.trim());
    if (!canRfid && !canBeacon) {
      message.warning('Provide an RFID, MAC, or IMEI (or a Status) before fetching.');
      return;
    }
    if (canRfid) loadRfid();
    else setRfidReads([]);
    if (canBeacon) loadBeacon();
    else setBeaconReads([]);
  }, [sensorFilter, rfidInput, macInput, imeiInput, statusInput, loadRfid, loadBeacon]);

  const loadAssetTimeline = useCallback(async () => {
    if (!ensureRange()) return;
    if (!timelineIdInput || !timelineIdInput.trim()) {
      message.warning(
        timelineSensor === 'RFID'
          ? 'Enter an RFID / Instance ID.'
          : 'Enter a Beacon MAC.'
      );
      return;
    }

    setTimelineLoading(true);
    try {
      let res;
      if (timelineSensor === 'RFID') {
        res = await fetchRfidData({ from: fromMs, to: toMs, rfid: timelineIdInput.trim() });
      } else {
        res = await fetchBeaconData({ from: fromMs, to: toMs, mac: timelineIdInput.trim() });
      }

      if (res && res.success === false) {
        message.error(res.error || 'Failed to fetch timeline data');
        setTimelineReads([]);
      } else {
        const payload = responsePayload(res);
        const normalized = timelineSensor === 'RFID'
          ? normalizeRfidList(payload)
          : normalizeBeaconList(payload);
        setTimelineReads(normalized.sort((a, b) => (b.eventTime || 0) - (a.eventTime || 0)));
        if (normalized.length === 0) {
          message.info('No reads found for this asset in the selected time range.');
        }
      }
    } catch (err) {
      showApiError(err, 'Failed to fetch timeline data');
      setTimelineReads([]);
    } finally {
      setTimelineLoading(false);
    }
  }, [ensureRange, timelineIdInput, timelineSensor, fromMs, toMs]);

  const loadReaderTimeline = useCallback(async () => {
    if (!ensureRange()) return;
    if (!readerImeiInput || !readerImeiInput.trim()) {
      message.warning('Enter a Reader IMEI.');
      return;
    }
    setReaderLoading(true);
    try {
      let res;
      if (readerSensor === 'RFID') {
        res = await fetchRfidData({ from: fromMs, to: toMs, imei: readerImeiInput.trim() });
      } else {
        res = await fetchBeaconData({ from: fromMs, to: toMs, imei: readerImeiInput.trim() });
      }

      if (res && res.success === false) {
        message.error(res.error || 'Failed to fetch reader data');
        setReaderReads([]);
      } else {
        const payload = responsePayload(res);
        const normalized = readerSensor === 'RFID'
          ? normalizeRfidList(payload)
          : normalizeBeaconList(payload);
        setReaderReads(normalized.sort((a, b) => (b.eventTime || 0) - (a.eventTime || 0)));
        if (normalized.length === 0) {
          message.info('No reads found for this reader in the selected time range.');
        }
      }
    } catch (err) {
      showApiError(err, 'Failed to fetch reader data');
      setReaderReads([]);
    } finally {
      setReaderLoading(false);
    }
  }, [ensureRange, readerImeiInput, readerSensor, fromMs, toMs]);

  // ---------- Drawer ----------
  const openDrawer = (record) => {
    setDrawerRecord(record);
    setDrawerOpen(true);
  };
  const closeDrawer = () => setDrawerOpen(false);

  // ---------- Table columns ----------
  const baseColumns = (sensorLabelKey) => [
    {
      title: 'Detected At',
      dataIndex: 'eventTime',
      key: 'eventTime',
      width: 170,
      render: (v) => formatTime(v),
      sorter: (a, b) => (a.eventTime || 0) - (b.eventTime || 0),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Detected Asset',
      dataIndex: 'assetIdentifier',
      key: 'assetIdentifier',
      width: 200,
      render: (v) => renderAssetCell(v, resolveTag(v))
    },
    {
      title: 'Reader (Asset)',
      dataIndex: 'readerImei',
      key: 'readerImei',
      width: 200,
      render: (v) => renderAssetCell(v, resolveReader(v))
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v) => v && v !== '-' ? <Tag>{v}</Tag> : '-'
    },
    {
      title: 'RSSI',
      dataIndex: 'rssi',
      key: 'rssi',
      width: 90,
      render: (v) => (v === null || v === undefined) ? '-' : `${v} dBm`
    },
    {
      title: 'Zone / Location',
      dataIndex: 'zone',
      key: 'zone',
      render: (v) => v || '-'
    },
    {
      title: 'Lat / Lng',
      key: 'latlng',
      width: 160,
      render: (_, r) => {
        if (r.latitude === null && r.longitude === null) return '-';
        return `${formatNumber(r.latitude)}, ${formatNumber(r.longitude)}`;
      }
    },
    {
      title: 'Classification',
      key: 'classification',
      width: 160,
      render: (_, r) => {
        const c = classifyPassiveAssetRead(r, {
          repeatedDetectionCount: repeatIndex.get(r.assetIdentifier) || 0
        });
        return classificationTag(c.classification);
      }
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, r) => (
        <Button size="small" onClick={() => openDrawer(r)}>
          Details
        </Button>
      )
    }
  ];

  const rfidColumns = baseColumns('RFID');
  const beaconColumns = baseColumns('BEACON');

  // ---------- Exceptions ----------
  const exceptions = useMemo(() => {
    return allReads
      .map((r) => {
        const c = classifyPassiveAssetRead(r, {
          repeatedDetectionCount: repeatIndex.get(r.assetIdentifier) || 0
        });
        return { ...r, classification: c.classification, classificationReason: c.reason };
      })
      .filter((r) =>
        r.classification === CLASSIFICATION.NEEDS_REVIEW ||
        r.classification === CLASSIFICATION.POSSIBLE_WRONG_ZONE ||
        r.classification === CLASSIFICATION.LIKELY_PASS_BY ||
        r.classification === CLASSIFICATION.INSUFFICIENT_CONTEXT
      );
  }, [allReads, repeatIndex]);

  const exceptionColumns = [
    {
      title: 'Detected At',
      dataIndex: 'eventTime',
      width: 170,
      render: (v) => formatTime(v)
    },
    {
      title: 'Sensor',
      dataIndex: 'sensorType',
      width: 100,
      render: (v) => sensorTag(v)
    },
    {
      title: 'Detected Asset',
      dataIndex: 'assetIdentifier',
      render: (v) => renderAssetCell(v, resolveTag(v))
    },
    {
      title: 'Reader (Asset)',
      dataIndex: 'readerImei',
      render: (v) => renderAssetCell(v, resolveReader(v))
    },
    {
      title: 'Classification',
      dataIndex: 'classification',
      render: (v) => classificationTag(v)
    },
    {
      title: 'Reason',
      dataIndex: 'classificationReason',
      render: (v) => v || '-'
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, r) => (
        <Button size="small" onClick={() => openDrawer(r)}>
          Details
        </Button>
      )
    }
  ];

  // ---------- Drawer body ----------
  const drawerClassification = drawerRecord
    ? classifyPassiveAssetRead(drawerRecord, {
        repeatedDetectionCount: repeatIndex.get(drawerRecord.assetIdentifier) || 0
      })
    : null;

  // ---------- Debug panel (last request + raw response) ----------
  const renderDebug = (debug) => {
    if (!debug) return null;
    let rawPreview;
    try {
      rawPreview = typeof debug.raw === 'string'
        ? debug.raw
        : JSON.stringify(debug.raw, null, 2);
    } catch (e) {
      rawPreview = String(debug.raw);
    }
    if (rawPreview && rawPreview.length > 4000) {
      rawPreview = `${rawPreview.slice(0, 4000)}\n… (truncated)`;
    }
    return (
      <Alert
        type={debug.ok ? (debug.extracted > 0 ? 'success' : 'info') : 'error'}
        style={{ marginBottom: 12 }}
        message={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
              GET {debug.endpoint}
            </span>
            <span>
              {debug.ok
                ? <Tag color={debug.extracted > 0 ? 'green' : 'orange'}>{debug.extracted} rows parsed</Tag>
                : <Tag color="red">request failed</Tag>}
              <Button size="small" onClick={() => setShowDebug((v) => !v)}>
                {showDebug ? 'Hide raw' : 'Show raw'}
              </Button>
            </span>
          </div>
        }
        description={
          showDebug ? (
            <pre style={{ fontSize: 11, maxHeight: 280, overflow: 'auto', background: '#fafafa', padding: 8, marginTop: 8 }}>
{debug.error ? `Error: ${debug.error}\n\n` : ''}{rawPreview || '(empty response body)'}
            </pre>
          ) : null
        }
      />
    );
  };

  // ---------- Render ----------
  return (
    <div className="passive-asset-visibility">
      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="Passive detection — historical only"
        description="Bluetooth beacon and RFID assets are only detected when they come near a GPS-enabled reader. Values shown below are last-known / historically detected, not live locations."
      />

      {assetIndexLoading && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          icon={<Icon type="sync" spin />}
          message="Loading asset directory to resolve sensor ids to asset codes…"
        />
      )}
      {assetIndexError && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message="Could not load asset directory"
          description={`Sensor ids will be shown as-is (unlinked). ${assetIndexError}`}
        />
      )}
      {assetIndex && !assetIndexLoading && (
        <Alert
          style={{ marginBottom: 16 }}
          type={assetIndex.stats && (assetIndex.stats.withBle > 0 || assetIndex.stats.withTagDevice > 0) ? 'success' : 'warning'}
          showIcon
          message={
            assetIndex.stats
              ? `Asset directory loaded — ${assetIndex.count} assets · ${assetIndex.stats.withBle} BLE · ${assetIndex.stats.withRfid} RFID · ${assetIndex.stats.withTagDevice} reader/GPS · ${assetIndex.stats.withDependents} with child devices indexed.`
              : `Asset directory loaded — ${assetIndex.count} assets indexed for sensor resolution.`
          }
          description={
            assetIndex.stats && assetIndex.stats.withBle === 0
              ? 'No assets in this list carry a bleTagId — BLE tags will show as unlinked. The asset-all response likely excludes the BLE child-device records (or they are paginated beyond the fetched set).'
              : undefined
          }
        />
      )}

      <Card title={<span><Icon type="filter" /> Filters</span>} style={{ marginBottom: 16 }}>
        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Date Range</div>
            <RangePicker
              style={{ width: '100%' }}
              showTime
              value={range}
              onChange={(v) => setRange(v || DEFAULT_RANGE())}
              format="YYYY-MM-DD HH:mm"
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Sensor Type</div>
            <Select
              style={{ width: '100%' }}
              value={sensorFilter}
              onChange={(v) => setSensorFilter(v)}
            >
              {SENSOR_OPTIONS.map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>RFID / Instance ID</div>
            <Input
              placeholder="e.g. 17997"
              value={rfidInput}
              onChange={(e) => setRfidInput(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Beacon MAC</div>
            <Input
              placeholder="e.g. AE0C08001D03"
              value={macInput}
              onChange={(e) => setMacInput(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Reader IMEI</div>
            <Input
              placeholder="e.g. 866943088122167"
              value={imeiInput}
              onChange={(e) => setImeiInput(e.target.value)}
              allowClear
            />
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} sm={12} md={4}>
            <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Status (optional)</div>
            <Select
              style={{ width: '100%' }}
              value={statusInput}
              onChange={(v) => setStatusInput(v)}
              placeholder="Any"
              allowClear
            >
              {STATUS_OPTIONS.map((s) => (
                <Option key={s} value={s}>{s}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button type="primary" icon="search" onClick={loadAll}>
              Fetch Reads
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              icon="reload"
              onClick={() => {
                setRfidReads([]);
                setBeaconReads([]);
                setRfidError(null);
                setBeaconError(null);
              }}
            >
              Clear
            </Button>
          </Col>
        </Row>
      </Card>

      <Tabs defaultActiveKey="overview" type="card">
        <TabPane tab="Overview" key="overview">
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col xs={12} sm={6}>
              <Card><Statistic title="Total Reads" value={kpis.totalReads} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card><Statistic title="Unique RFID Tags" value={kpis.uniqueTags} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card><Statistic title="Unique Beacon MACs" value={kpis.uniqueMacs} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card><Statistic title="Unique Reader Devices" value={kpis.uniqueReaders} /></Card>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12} sm={6}>
              <Card><Statistic title="Seen in Last 1 Hour" value={kpis.seen1h} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card><Statistic title="Seen in Last 24 Hours" value={kpis.seen24h} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card><Statistic title="Low Confidence / Weak Signal" value={kpis.weak} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card><Statistic title="Needs Review" value={kpis.needsReview} valueStyle={{ color: '#fa8c16' }} /></Card>
            </Col>
          </Row>
          <Alert
            style={{ marginTop: 16 }}
            type="warning"
            showIcon
            message="Charts pending enriched APIs"
            description="Reads-over-time, top tags, and reader-coverage charts will appear once enriched visibility-summary APIs are available."
          />
        </TabPane>

        <TabPane tab={`RFID Reads (${rfidReads.length})`} key="rfid">
          {rfidError && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={rfidError} />
          )}
          {renderDebug(rfidDebug)}
          {rfidReads.length > 0 && (
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Checkbox
                checked={hideEmptyRfid}
                onChange={(e) => setHideEmptyRfid(e.target.checked)}
              >
                Hide reads with no RFID tag found
              </Checkbox>
              <span style={{ fontSize: 12, color: '#888' }}>
                Showing {displayedRfidReads.length} of {rfidReads.length}
                {emptyRfidCount > 0 ? ` · ${emptyRfidCount} empty-tag read${emptyRfidCount === 1 ? '' : 's'} hidden` : ''}
              </span>
            </div>
          )}
          <Spin spinning={loadingRfid}>
            {rfidReads.length === 0 && !loadingRfid ? (
              renderEmpty('Enter RFID, Reader IMEI, or pick a Status, then click Fetch Reads.')
            ) : displayedRfidReads.length === 0 && !loadingRfid ? (
              renderEmpty('All reads in this range had no RFID tag. Uncheck the filter above to see them.')
            ) : (
              <Table
                rowKey="id"
                size="small"
                columns={rfidColumns}
                dataSource={displayedRfidReads}
                pagination={{ pageSize: 25, showSizeChanger: true }}
                scroll={{ x: 1100 }}
              />
            )}
          </Spin>
        </TabPane>

        <TabPane tab={`Beacon Reads (${beaconReads.length})`} key="beacon">
          {beaconError && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={beaconError} />
          )}
          {renderDebug(beaconDebug)}
          <Spin spinning={loadingBeacon}>
            {beaconReads.length === 0 && !loadingBeacon ? (
              renderEmpty('Enter Beacon MAC or Device IMEI, then click Fetch Reads.')
            ) : (
              <Table
                rowKey="id"
                size="small"
                columns={beaconColumns}
                dataSource={beaconReads}
                pagination={{ pageSize: 25, showSizeChanger: true }}
                scroll={{ x: 1100 }}
              />
            )}
          </Spin>
        </TabPane>

        <TabPane tab="Asset Timeline" key="asset-timeline">
          <Card style={{ marginBottom: 12 }}>
            <Row gutter={12}>
              <Col xs={24} sm={6}>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Sensor Type</div>
                <Select style={{ width: '100%' }} value={timelineSensor} onChange={setTimelineSensor}>
                  <Option value="RFID">RFID</Option>
                  <Option value="BEACON">Beacon</Option>
                </Select>
              </Col>
              <Col xs={24} sm={10}>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>
                  {timelineSensor === 'RFID' ? 'RFID / Instance ID' : 'Beacon MAC'}
                </div>
                <Input
                  placeholder={timelineSensor === 'RFID' ? 'e.g. 17997' : 'e.g. AE0C08001D03'}
                  value={timelineIdInput}
                  onChange={(e) => setTimelineIdInput(e.target.value)}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={8} style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button type="primary" icon="search" onClick={loadAssetTimeline}>
                  Load Timeline
                </Button>
              </Col>
            </Row>
          </Card>

          <Spin spinning={timelineLoading}>
            {timelineReads.length === 0 && !timelineLoading ? (
              renderEmpty('Pick a sensor type and tag/MAC, then click Load Timeline.')
            ) : (
              <Row gutter={16}>
                <Col xs={24} md={10}>
                  <Card title="Summary" size="small">
                    <Row gutter={8}>
                      <Col span={12}><Statistic title="Seen Count" value={timelineReads.length} /></Col>
                      <Col span={12}>
                        <Statistic
                          title="Last Seen"
                          value={formatTime(timelineReads[0]?.eventTime)}
                          valueStyle={{ fontSize: 14 }}
                        />
                      </Col>
                    </Row>
                    <Row gutter={8} style={{ marginTop: 8 }}>
                      <Col span={12}>
                        <Statistic
                          title="First Seen"
                          value={formatTime(timelineReads[timelineReads.length - 1]?.eventTime)}
                          valueStyle={{ fontSize: 14 }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Last Reader"
                          value={assetLabel(timelineReads[0]?.readerImei, resolveReader(timelineReads[0]?.readerImei))}
                          valueStyle={{ fontSize: 14 }}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col xs={24} md={14}>
                  <Card title="Timeline" size="small">
                    <Timeline>
                      {timelineReads.slice(0, 100).map((r) => (
                        <Timeline.Item key={r.id} color={r.rssi !== null && r.rssi < -85 ? 'red' : 'blue'}>
                          <div><strong>{formatTime(r.eventTime)}</strong></div>
                          <div>
                            Last detected near reader {assetLabel(r.readerImei, resolveReader(r.readerImei))}
                            {r.zone && r.zone !== '-' ? ` at ${r.zone}` : ''}
                          </div>
                          <div style={{ fontSize: 12, color: '#888' }}>
                            Status: {r.status || '-'} {r.rssi !== null && r.rssi !== undefined ? `· Signal: ${r.rssi} dBm` : ''}
                          </div>
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  </Card>
                </Col>
              </Row>
            )}
            {timelineReads.length > 0 && (
              <Card title="Raw Reads" size="small" style={{ marginTop: 12 }}>
                <Table
                  rowKey="id"
                  size="small"
                  columns={timelineSensor === 'RFID' ? rfidColumns : beaconColumns}
                  dataSource={timelineReads}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 1100 }}
                />
              </Card>
            )}
          </Spin>
        </TabPane>

        <TabPane tab="Reader Timeline" key="reader-timeline">
          <Card style={{ marginBottom: 12 }}>
            <Row gutter={12}>
              <Col xs={24} sm={6}>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Sensor Type</div>
                <Select style={{ width: '100%' }} value={readerSensor} onChange={setReaderSensor}>
                  <Option value="RFID">RFID</Option>
                  <Option value="BEACON">Beacon</Option>
                </Select>
              </Col>
              <Col xs={24} sm={10}>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Reader IMEI</div>
                <Input
                  placeholder="e.g. 866943088122167"
                  value={readerImeiInput}
                  onChange={(e) => setReaderImeiInput(e.target.value)}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={8} style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button type="primary" icon="search" onClick={loadReaderTimeline}>
                  Load Reader
                </Button>
              </Col>
            </Row>
          </Card>

          <Spin spinning={readerLoading}>
            {readerReads.length === 0 && !readerLoading ? (
              renderEmpty('Enter Reader IMEI, then click Load Reader.')
            ) : (
              <>
                <Row gutter={16} style={{ marginBottom: 12 }}>
                  <Col xs={12} sm={6}>
                    <Card><Statistic title="Total Reads" value={readerReads.length} /></Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card>
                      <Statistic
                        title="Unique Assets Detected"
                        value={new Set(readerReads.map((r) => r.assetIdentifier).filter((v) => v && v !== '-')).size}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card>
                      <Statistic
                        title="Last Read"
                        value={formatTime(readerReads[0]?.eventTime)}
                        valueStyle={{ fontSize: 14 }}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card>
                      <Statistic
                        title="First Read"
                        value={formatTime(readerReads[readerReads.length - 1]?.eventTime)}
                        valueStyle={{ fontSize: 14 }}
                      />
                    </Card>
                  </Col>
                </Row>
                <Table
                  rowKey="id"
                  size="small"
                  columns={readerSensor === 'RFID' ? rfidColumns : beaconColumns}
                  dataSource={readerReads}
                  pagination={{ pageSize: 25, showSizeChanger: true }}
                  scroll={{ x: 1100 }}
                />
              </>
            )}
          </Spin>
        </TabPane>

        <TabPane tab={`Map (${activeMapPoints.length})`} key="map">
          <Card
            size="small"
            style={{ marginBottom: 12 }}
            title={
              <span>
                <Icon type="environment" /> Detection Map
                {perspective.mode === 'reader' && (
                  <Tag color="purple" style={{ marginLeft: 8 }}>Reader / tug view · {perspective.assets} assets</Tag>
                )}
                {perspective.mode === 'asset' && (
                  <Tag color="cyan" style={{ marginLeft: 8 }}>Asset view · {perspective.readers} readers</Tag>
                )}
                {perspective.mode === 'mixed' && (
                  <Tag style={{ marginLeft: 8 }}>Mixed · {perspective.assets} assets / {perspective.readers} readers</Tag>
                )}
                <span style={{ fontWeight: 400, fontSize: 12, color: '#888', marginLeft: 8 }}>
                  {usingClusters
                    ? 'Each point = a reader position; bubble size = assets moving WITH it then (FOUND→LOST active)'
                    : usingJourney
                      ? 'Cyan = where the tag dwelled; blue = towed move (reader GPS); dotted = untracked move'
                      : usingTrack
                        ? 'Line = reader GPS track while the asset was FOUND (riding with the reader)'
                        : 'Circles = reader uncertainty zone (beacon is somewhere inside)'}
                </span>
              </span>
            }
          >
            <Row gutter={12} align="middle" type="flex" style={{ rowGap: 8 }}>
              <Col>
                <span style={{ fontSize: 12, color: '#666', marginRight: 6 }}>Source:</span>
                <Radio.Group
                  value={mapSensor}
                  onChange={(e) => setMapSensor(e.target.value)}
                  size="small"
                >
                  <Radio.Button value="BEACON">Beacon</Radio.Button>
                  <Radio.Button value="RFID">RFID</Radio.Button>
                  <Radio.Button value="ALL">All</Radio.Button>
                </Radio.Group>
              </Col>
              <Col>
                <Checkbox
                  checked={showReaderTrack}
                  disabled={mapSensor === 'RFID' || clusterByScan}
                  onChange={(e) => setShowReaderTrack(e.target.checked)}
                >
                  Show reader GPS track (FOUND→LOST)
                </Checkbox>
                {trackLoading && <Icon type="sync" spin style={{ marginLeft: 6 }} />}
              </Col>
              <Col>
                <Checkbox
                  checked={clusterByScan}
                  disabled={mapSensor === 'RFID'}
                  onChange={(e) => setClusterByScan(e.target.checked)}
                >
                  Cluster by reader scan (concurrent assets)
                </Checkbox>
              </Col>
              <Col>
                <Button.Group size="small">
                  <Button
                    icon="fast-backward"
                    onClick={() => {
                      setIsPlaying(false);
                      setPlaybackIndex(0);
                    }}
                    disabled={activeMapPoints.length === 0}
                  >
                    Reset
                  </Button>
                  <Button
                    icon="step-backward"
                    onClick={() => {
                      setIsPlaying(false);
                      setPlaybackIndex((i) => Math.max(0, i - 1));
                    }}
                    disabled={activeMapPoints.length === 0 || playbackIndex <= 0}
                  >
                    Back
                  </Button>
                  <Button
                    type={isPlaying ? 'default' : 'primary'}
                    icon={isPlaying ? 'pause' : 'caret-right'}
                    onClick={() => {
                      if (activeMapPoints.length === 0) return;
                      if (playbackIndex >= activeMapPoints.length - 1) setPlaybackIndex(0);
                      setIsPlaying((v) => !v);
                    }}
                    disabled={activeMapPoints.length <= 1}
                  >
                    {isPlaying ? 'Pause' : 'Play'}
                  </Button>
                  <Button
                    icon="step-forward"
                    onClick={() => {
                      setIsPlaying(false);
                      setPlaybackIndex((i) => Math.min(activeMapPoints.length - 1, i + 1));
                    }}
                    disabled={activeMapPoints.length === 0 || playbackIndex >= activeMapPoints.length - 1}
                  >
                    Step
                  </Button>
                </Button.Group>
              </Col>
              <Col>
                <span style={{ fontSize: 12, color: '#666', marginRight: 6 }}>Speed:</span>
                <Select
                  size="small"
                  value={playbackSpeed}
                  onChange={(v) => setPlaybackSpeed(v)}
                  style={{ width: 80 }}
                >
                  <Option value={0.5}>0.5x</Option>
                  <Option value={1}>1x</Option>
                  <Option value={2}>2x</Option>
                  <Option value={4}>4x</Option>
                  <Option value={8}>8x</Option>
                </Select>
              </Col>
              <Col>
                <Button
                  size="small"
                  icon="table"
                  type={dataPanelOpen ? 'primary' : 'default'}
                  onClick={() => setDataPanelOpen((v) => !v)}
                  disabled={activeMapPoints.length === 0}
                >
                  Data panel
                </Button>
              </Col>
              {activePoint && (
                <Col flex="auto">
                  {usingJourney ? (
                    <span style={{ fontSize: 12, color: '#444' }}>
                      <strong>{assetLabel(activePoint.assetIdentifier, resolveTag(activePoint.assetIdentifier))}</strong>
                      &nbsp;· {moment(activePoint.ts).format('YYYY-MM-DD HH:mm:ss')}
                      &nbsp;
                      {activePoint.dwell ? (
                        <Tag color="cyan" style={{ marginLeft: 4 }}>
                          dwelling {moment(activePoint.dwellStartTs).format('HH:mm')}–{moment(activePoint.dwellEndTs).format('HH:mm')}
                        </Tag>
                      ) : (
                        <Tag color="blue" style={{ marginLeft: 4 }}>
                          moving with {assetLabel(activePoint.readerImei, resolveReader(activePoint.readerImei))}
                          {activePoint.speed !== null && activePoint.speed !== undefined ? ` · ${Number(activePoint.speed).toFixed(0)} km/h` : ''}
                        </Tag>
                      )}
                    </span>
                  ) : (usingClusters || usingTrack) ? (
                    <span style={{ fontSize: 12, color: '#444' }}>
                      <strong>Reader {assetLabel(activePoint.readerImei, resolveReader(activePoint.readerImei))}</strong>
                      &nbsp;· {moment(activePoint.ts).format('YYYY-MM-DD HH:mm:ss')}
                      {usingTrack && !activePoint.noGps && activePoint.speed !== null && activePoint.speed !== undefined
                        ? ` · ${Number(activePoint.speed).toFixed(0)} km/h`
                        : ''}
                      {usingTrack && activePoint.noGps ? ' · ⚠ no GPS' : ''}
                      &nbsp;
                      {usingTrack ? (
                        activePoint.count ? (
                          <>
                            <Tag color="blue" style={{ marginLeft: 4 }}>{activePoint.towedCount || 0} towed</Tag>
                            <Tag color="orange">{activePoint.count - (activePoint.towedCount || 0)} detected</Tag>
                          </>
                        ) : (
                          <Tag style={{ marginLeft: 4 }}>empty</Tag>
                        )
                      ) : (
                        <Tag color={activePoint.count ? 'blue' : 'default'} style={{ marginLeft: 4 }}>
                          {activePoint.count} moving with reader
                        </Tag>
                      )}
                      <span style={{ color: '#888' }}>
                        {(activePoint.detections || []).slice(0, 4).map((d) =>
                          assetLabel(d.assetIdentifier, resolveTag(d.assetIdentifier))).join(', ')}
                        {(activePoint.detections || []).length > 4 ? ` +${activePoint.detections.length - 4} more` : ''}
                      </span>
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, color: '#444' }}>
                      <strong>{assetLabel(activePoint.assetIdentifier, resolveTag(activePoint.assetIdentifier))}</strong>
                      &nbsp;· {moment(activePoint.ts).format('YYYY-MM-DD HH:mm:ss')}
                      &nbsp;· Reader {assetLabel(activePoint.readerImei, resolveReader(activePoint.readerImei))}
                      {activePoint.rssi !== null && activePoint.rssi !== undefined ? ` · ${activePoint.rssi} dBm` : ''}
                      {activePoint.distance !== null && activePoint.distance !== undefined
                        ? ` · ~${activePoint.distance.toFixed(1)} m`
                        : ''}
                      {activePoint.zone && activePoint.zone !== '-' ? ` · ${activePoint.zone}` : ''}
                      &nbsp;
                      <Tag color={CONFIDENCE_COLORS[activePoint.confidence]} style={{ marginLeft: 4 }}>
                        {activePoint.confidence}
                      </Tag>
                    </span>
                  )}
                </Col>
              )}
            </Row>

            {!usingTrack && !usingClusters && (
              <Row gutter={12} align="middle" type="flex" style={{ marginTop: 10, rowGap: 8 }}>
                <Col>
                  <span style={{ fontSize: 12, color: '#666' }}>
                    RSSI uncertainty (radius around reader = where the beacon likely is):
                  </span>
                </Col>
                <Col>
                  <span style={{ fontSize: 12, color: '#666', marginRight: 4 }}>Measured Power (dBm @1m):</span>
                  <Input
                    size="small"
                    style={{ width: 70 }}
                    type="number"
                    value={measuredPower}
                    onChange={(e) => setMeasuredPower(Number(e.target.value))}
                  />
                </Col>
                <Col>
                  <span style={{ fontSize: 12, color: '#666', marginRight: 4 }}>Environment n:</span>
                  <Input
                    size="small"
                    style={{ width: 70 }}
                    type="number"
                    step={0.1}
                    value={environmentN}
                    onChange={(e) => setEnvironmentN(Number(e.target.value))}
                  />
                </Col>
                <Col>
                  <span style={{ fontSize: 12, color: '#666', marginRight: 4 }}>RSSI sign:</span>
                  <Select
                    size="small"
                    value={signedAsNegative ? 'flip' : 'asis'}
                    onChange={(v) => setSignedAsNegative(v === 'flip')}
                    style={{ width: 150 }}
                  >
                    <Option value="flip">Flip (17 → −17 dBm)</Option>
                    <Option value="asis">As-is (signed dBm)</Option>
                  </Select>
                </Col>
              </Row>
            )}

            {showReaderTrack && trackError && (
              <Alert type="warning" showIcon style={{ marginTop: 10 }} message={trackError} />
            )}
            {showReaderTrack && trackNotice && (
              <Alert type="info" showIcon style={{ marginTop: 10 }} message={trackNotice} />
            )}
            {showReaderTrack && !usingTrack && !usingJourney && !usingClusters && !trackLoading && !trackError && beaconReads.length > 0 && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 10 }}
                message="No movement to plot for this selection. Showing point detections instead."
              />
            )}
            {usingJourney && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                {journeyDwells.length} dwell location(s)
                · {journeyMoveSegments.length} towed move(s)
                {journeyJumps.length > 0 && (
                  <span style={{ marginLeft: 8, color: '#fa8c16' }}>┄┄ dotted = untracked move ({journeyJumps.length})</span>
                )}
                <span style={{ marginLeft: 8 }}>
                  <Tag color="cyan">dwell</Tag><Tag color="blue">towed move</Tag>
                </span>
              </div>
            )}
            {usingTrack && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                {readerTracks.filter((t) => (t.points || []).length > 0).length} reader track(s)
                · {trackPoints.length} GPS points
                {(() => {
                  const towed = new Set();
                  const detected = new Set();
                  readerTracks.forEach((t) => (t.intervals || []).forEach((i) => {
                    if (i.towed) towed.add(i.assetIdentifier);
                    else detected.add(i.assetIdentifier);
                  }));
                  // An asset towed in any interval counts as towed, not detected-only.
                  const detectedOnly = [...detected].filter((a) => !towed.has(a)).length;
                  return ` · ${towed.size} towed · ${detectedOnly} detected-only`;
                })()}
                {(() => {
                  const peak = trackPoints.reduce((m, p) => Math.max(m, p.towedCount || 0), 0);
                  return peak > 1 ? ` · up to ${peak} towed at once` : '';
                })()}
                {(() => {
                  const noGps = trackPoints.filter((p) => p.noGps).length;
                  return noGps > 0
                    ? ` · ${noGps} held at last-known (no GPS signal)`
                    : '';
                })()}
                {baseTrack.jumps.length > 0 && (
                  <span style={{ marginLeft: 8, color: '#fa8c16' }}>
                    ┄┄ dotted = untracked move ({baseTrack.jumps.length}) e.g. towed by a non-GPS vehicle
                  </span>
                )}
              </div>
            )}

            {usingClusters && detectionSummary && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                {clusterPoints.length} reader scan(s)
                · {detectionSummary.distinctAssets} distinct asset(s)
                · max concurrent {clusterPoints.reduce((m, c) => Math.max(m, c.count), 0)}
                &nbsp;
                <Tag color="geekblue">towed {detectionSummary.towed.length}</Tag>
                <Tag>pass-by {detectionSummary.passBy.length}</Tag>
                {detectionSummary.towed.length > 0 && (
                  <span style={{ color: '#888' }}>
                    towed: {detectionSummary.towed.slice(0, 6).map((a) =>
                      assetLabel(a.assetIdentifier, resolveTag(a.assetIdentifier))).join(', ')}
                    {detectionSummary.towed.length > 6 ? ` +${detectionSummary.towed.length - 6} more` : ''}
                  </span>
                )}
              </div>
            )}

            {activeMapPoints.length > 1 && (
              <div style={{ marginTop: 10 }}>
                <Slider
                  min={0}
                  max={activeMapPoints.length - 1}
                  value={playbackIndex}
                  onChange={(v) => {
                    setIsPlaying(false);
                    setPlaybackIndex(v);
                  }}
                  tipFormatter={(v) => {
                    const p = activeMapPoints[v];
                    return p ? moment(p.ts).format('HH:mm:ss') : '';
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888' }}>
                  <span>{moment(activeMapPoints[0].ts).format('YYYY-MM-DD HH:mm:ss')}</span>
                  <span>{playbackIndex + 1} / {activeMapPoints.length}</span>
                  <span>{moment(activeMapPoints[activeMapPoints.length - 1].ts).format('YYYY-MM-DD HH:mm:ss')}</span>
                </div>
              </div>
            )}
          </Card>

          {activeMapPoints.length === 0 ? (
            renderEmpty(
              clusterByScan
                ? 'No reader scans with valid lat/long to cluster. Fetch beacon reads (e.g. by Reader IMEI) first.'
                : (usingTrack || showReaderTrack)
                  ? 'No reader GPS track to plot. Ensure beacon reads with FOUND/LOST status are loaded and the reader is linked to an asset.'
                  : 'No reads with valid lat/long for the selected source. Fetch reads first.'
            )
          ) : (
            <div style={{ height: 540, border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
              <LeafletMap
                ref={mapRef}
                center={[activeMapPoints[0].lat, activeMapPoints[0].lng]}
                zoom={15}
                maxZoom={20}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer attribution={ESRI_TILE_ATTRIBUTION} url={ESRI_TILE_URL} maxZoom={20} />

                {renderGeoFencePolygons(geoFences)}

                {usingClusters ? (
                  <>
                    {/* Reader path across scans (coarse, dashed). */}
                    {clusterPoints.length >= 2 && (
                      <Polyline
                        positions={clusterPoints.map((c) => [c.lat, c.lng])}
                        color="#bfbfbf"
                        weight={1}
                        opacity={0.5}
                        dashArray="4,6"
                      />
                    )}

                    {/* One bubble per scan; size grows with concurrent asset count. */}
                    {clusterPoints.map((c, ci) => (
                      <CircleMarker
                        key={`cl-${c.id}`}
                        center={[c.lat, c.lng]}
                        radius={5 + Math.min(18, c.count * 2)}
                        color={ci === playbackIndex ? '#722ed1' : '#1890ff'}
                        fillColor={ci === playbackIndex ? '#722ed1' : '#1890ff'}
                        fillOpacity={ci === playbackIndex ? 0.45 : 0.2}
                        weight={ci === playbackIndex ? 2 : 1}
                        onClick={() => { setIsPlaying(false); setPlaybackIndex(ci); }}
                      >
                        <LeafletTooltip direction="top" offset={[0, -6]} opacity={0.95}>
                          <span style={{ fontSize: 11 }}>
                            <strong>{c.count} asset{c.count === 1 ? '' : 's'}</strong>
                            {' @ '}{moment(c.ts).format('HH:mm:ss')}
                          </span>
                        </LeafletTooltip>
                      </CircleMarker>
                    ))}

                    {/* Active scan: pin + permanent manifest tooltip. */}
                    {activePoint && (
                      <Marker position={[activePoint.lat, activePoint.lng]} icon={playbackPinIcon}>
                        <LeafletTooltip permanent direction="top" offset={[0, -22]} opacity={0.97}>
                          <span style={{ fontSize: 11 }}>
                            <strong>Reader {assetLabel(activePoint.readerImei, resolveReader(activePoint.readerImei))}</strong>
                            <br />
                            {moment(activePoint.ts).format('HH:mm:ss')} · {activePoint.count} on board
                            <br />
                            {(activePoint.detections || []).slice(0, 8).map((d, di) => (
                              <span key={di} style={{ display: 'block' }}>
                                • {assetLabel(d.assetIdentifier, resolveTag(d.assetIdentifier))}
                                {d.rssi !== null && d.rssi !== undefined ? ` (${d.rssi} dBm)` : ''}
                              </span>
                            ))}
                            {(activePoint.detections || []).length > 8
                              ? `…+${activePoint.detections.length - 8} more` : ''}
                          </span>
                        </LeafletTooltip>
                      </Marker>
                    )}
                  </>
                ) : usingJourney ? (
                  <>
                    {/* Untracked transitions between dwells / legs (dotted). */}
                    {journeyJumps.map((seg, ji) => (
                      <Polyline key={`jjump-${ji}`} positions={seg} color="#fa8c16" weight={2} opacity={0.7} dashArray="2,8" />
                    ))}

                    {/* Towed moves drawn from the towing reader's GPS (solid blue). */}
                    {journeyMoveSegments.map((seg, mi) => (
                      <Polyline key={`jmove-${mi}`} positions={seg.positions} color="#1890ff" weight={4} opacity={0.75} />
                    ))}

                    {/* Played-so-far highlight. */}
                    {playedTrack.runs.map((run, ri) => (
                      <Polyline key={`jplayed-run-${ri}`} positions={run} color="#722ed1" weight={4} opacity={0.9} />
                    ))}
                    {playedTrack.jumps.map((seg, ji) => (
                      <Polyline key={`jplayed-jump-${ji}`} positions={seg} color="#722ed1" weight={3} opacity={0.8} dashArray="2,8" />
                    ))}

                    {/* Dwell locations — where the tag stayed (size ∝ detections). */}
                    {journeyDwells.map((d, di) => (
                      <CircleMarker
                        key={`dwell-${d.id || di}`}
                        center={[d.lat, d.lng]}
                        radius={6 + Math.min(14, Math.round(Math.log2((d.dwellCount || 1) + 1) * 3))}
                        color="#13c2c2"
                        fillColor="#13c2c2"
                        fillOpacity={0.25}
                        weight={2}
                      >
                        <LeafletTooltip direction="top">
                          <span style={{ fontSize: 11 }}>
                            Dwell · {moment(d.dwellStartTs).format('HH:mm')}–{moment(d.dwellEndTs).format('HH:mm')}
                            {' · '}{d.dwellCount} detections
                          </span>
                        </LeafletTooltip>
                      </CircleMarker>
                    ))}

                    {/* Active position marker. */}
                    {activePoint && (
                      <Marker position={[activePoint.lat, activePoint.lng]} icon={activePoint.noGps ? noGpsPinIcon : playbackPinIcon}>
                        <LeafletTooltip permanent direction="top" offset={[0, -22]} opacity={0.95}>
                          <span style={{ fontSize: 11, fontWeight: 600 }}>
                            {assetLabel(activePoint.assetIdentifier, resolveTag(activePoint.assetIdentifier))}
                            <br />
                            {activePoint.dwell ? (
                              <span style={{ fontWeight: 400 }}>
                                Dwelling · {moment(activePoint.dwellStartTs).format('HH:mm')}–{moment(activePoint.dwellEndTs).format('HH:mm')}
                              </span>
                            ) : (
                              <span style={{ fontWeight: 400, color: '#1890ff' }}>
                                Moving with {assetLabel(activePoint.readerImei, resolveReader(activePoint.readerImei))}
                                {activePoint.speed !== null && activePoint.speed !== undefined ? ` · ${Number(activePoint.speed).toFixed(0)} km/h` : ''}
                              </span>
                            )}
                          </span>
                        </LeafletTooltip>
                      </Marker>
                    )}
                  </>
                ) : usingTrack ? (
                  <>
                    {/* Full reader route over the period (grey = moving, carrying nothing). */}
                    {baseTrack.runs.map((run, ri) => (
                      <Polyline
                        key={`base-run-${ri}`}
                        positions={run.positions}
                        color="#8c8c8c"
                        weight={2}
                        opacity={0.5}
                      />
                    ))}

                    {/* Loaded stretches: blue = towing (moved with reader),
                        orange = detected only (present but reader ~stationary). */}
                    {routeSegments.map((seg, ci) => (
                      <Polyline
                        key={`route-${ci}`}
                        positions={seg.positions}
                        color={seg.state === 'towing' ? '#1890ff' : '#faad14'}
                        weight={4}
                        opacity={0.75}
                      />
                    ))}

                    {/* Unlinked readers (no GPS track): straight dashed FOUND→LOST line per asset. */}
                    {readerTracks.map((t, ti) => {
                      if ((t.points || []).length >= 1) return null;
                      return (t.intervals || []).map((itv, ii) => {
                        const positions = [itv.startRead, itv.endRead]
                          .filter((r) => r && r.latitude != null && r.longitude != null)
                          .map((r) => [Number(r.latitude), Number(r.longitude)]);
                        if (positions.length < 2) return null;
                        return (
                          <Polyline
                            key={`unlinked-${t.key}-${ti}-${ii}`}
                            positions={positions}
                            color="#bfbfbf"
                            weight={2}
                            opacity={0.5}
                            dashArray="6,8"
                          />
                        );
                      });
                    })}

                    {/* Untracked jumps (e.g. towed by a non-GPS vehicle): dotted. */}
                    {baseTrack.jumps.map((seg, si) => (
                      <Polyline
                        key={`jump-${si}`}
                        positions={seg.positions}
                        color="#fa8c16"
                        weight={2}
                        opacity={0.7}
                        dashArray="2,8"
                      />
                    ))}

                    {/* Played-so-far highlight: solid within a tracked window... */}
                    {playedTrack.runs.map((run, ri) => (
                      <Polyline
                        key={`played-run-${ri}`}
                        positions={run}
                        color="#722ed1"
                        weight={4}
                        opacity={0.9}
                      />
                    ))}
                    {/* ...dotted across an untracked jump. */}
                    {playedTrack.jumps.map((seg, ji) => (
                      <Polyline
                        key={`played-jump-${ji}`}
                        positions={seg}
                        color="#722ed1"
                        weight={3}
                        opacity={0.8}
                        dashArray="2,8"
                      />
                    ))}

                    {/* FOUND (green) / LOST (red) endpoints — one per asset board/leave,
                        positioned at the reader's location when the event occurred. */}
                    {readerTracks.map((t, ti) => (t.intervals || []).map((itv, ii) => {
                      const first = itv.startRead && itv.startRead.latitude != null
                        ? { lat: Number(itv.startRead.latitude), lng: Number(itv.startRead.longitude) } : null;
                      const last = itv.endRead && itv.endRead.latitude != null
                        ? { lat: Number(itv.endRead.latitude), lng: Number(itv.endRead.longitude) } : null;
                      return (
                        <React.Fragment key={`ep-${t.key}-${ti}-${ii}`}>
                          {first && (
                            <Circle center={[first.lat, first.lng]} radius={6} color="#52c41a"
                              fillColor="#52c41a" fillOpacity={0.9} weight={2}>
                              <LeafletTooltip direction="top">
                                <span style={{ fontSize: 11 }}>
                                  FOUND {assetLabel(itv.assetIdentifier, resolveTag(itv.assetIdentifier))}
                                </span>
                              </LeafletTooltip>
                            </Circle>
                          )}
                          {last && !itv.open && (
                            <Circle center={[last.lat, last.lng]} radius={6} color="#ff4d4f"
                              fillColor="#ff4d4f" fillOpacity={0.9} weight={2}>
                              <LeafletTooltip direction="top">
                                <span style={{ fontSize: 11 }}>
                                  LOST {assetLabel(itv.assetIdentifier, resolveTag(itv.assetIdentifier))}
                                </span>
                              </LeafletTooltip>
                            </Circle>
                          )}
                        </React.Fragment>
                      );
                    }))}

                    {/* Moving asset marker. */}
                    {activePoint && (
                      <Marker
                        position={[activePoint.lat, activePoint.lng]}
                        icon={activePoint.noGps ? noGpsPinIcon : playbackPinIcon}
                      >
                        <LeafletTooltip permanent direction="top" offset={[0, -22]} opacity={0.95}>
                          <span style={{ fontSize: 11 }}>
                            <strong>Reader {assetLabel(activePoint.readerImei, resolveReader(activePoint.readerImei))}</strong>
                            <br />
                            {moment(activePoint.ts).format('HH:mm:ss')}
                            {activePoint.noGps
                              ? ' · ⚠ no GPS (last known)'
                              : (activePoint.speed !== null && activePoint.speed !== undefined
                                ? ` · ${Number(activePoint.speed).toFixed(0)} km/h` : '')}
                            {' · '}<strong>
                              {activePoint.count
                                ? `${activePoint.towedCount || 0} towed · ${activePoint.count - (activePoint.towedCount || 0)} detected`
                                : 'empty'}
                            </strong>
                            {(activePoint.detections || []).length > 0 && <br />}
                            {(activePoint.detections || []).slice(0, 8).map((d, di) => (
                              <span key={di} style={{ display: 'block', color: d.towed ? '#1890ff' : '#fa8c16' }}>
                                {d.towed ? '➜ ' : '• '}
                                {assetLabel(d.assetIdentifier, resolveTag(d.assetIdentifier))}
                                {d.rssi !== null && d.rssi !== undefined ? ` (${d.rssi} dBm)` : ''}
                                {d.towed ? '' : ' · detected'}
                              </span>
                            ))}
                            {(activePoint.detections || []).length > 8
                              ? `…+${activePoint.detections.length - 8} more` : ''}
                          </span>
                        </LeafletTooltip>
                      </Marker>
                    )}
                  </>
                ) : (
                  <>
                    {/* Faint dashed line showing the sequence in which readers detected
                        the asset — NOT the beacon's actual path. */}
                    {mapPoints.length >= 2 && (
                      <Polyline
                        positions={mapPoints.map((p) => [p.lat, p.lng])}
                        color="#bfbfbf"
                        weight={1}
                        opacity={0.45}
                        dashArray="4,6"
                      />
                    )}

                    {/* Past (already played) uncertainty circles — faint */}
                    {mapPoints.slice(0, Math.max(0, playbackIndex)).map((p, idx) => {
                      if (p.distance === null || p.distance === undefined) return null;
                      const color = CONFIDENCE_COLORS[p.confidence] || '#bfbfbf';
                      return (
                        <Circle
                          key={`past-${p.id || idx}`}
                          center={[p.lat, p.lng]}
                          radius={p.distance}
                          color={color}
                          fillColor={color}
                          fillOpacity={0.05}
                          weight={1}
                          opacity={0.35}
                        />
                      );
                    })}

                    {/* Reader location dots for every detection */}
                    {mapPoints.map((p, idx) => (
                      <Circle
                        key={`reader-${p.id || idx}`}
                        center={[p.lat, p.lng]}
                        radius={1.5}
                        color="#262626"
                        fillColor="#262626"
                        fillOpacity={1}
                        weight={1}
                      />
                    ))}

                    {/* Active uncertainty circle — the beacon is somewhere inside this */}
                    {activePoint && activePoint.distance !== null && activePoint.distance !== undefined && (
                      <Circle
                        center={[activePoint.lat, activePoint.lng]}
                        radius={activePoint.distance}
                        color={CONFIDENCE_COLORS[activePoint.confidence] || '#1890ff'}
                        fillColor={CONFIDENCE_COLORS[activePoint.confidence] || '#1890ff'}
                        fillOpacity={0.18}
                        weight={2}
                      />
                    )}

                    {/* Pin marks the reader (NOT the beacon). Tooltip names it explicitly. */}
                    {activePoint && (
                      <Marker position={[activePoint.lat, activePoint.lng]} icon={playbackPinIcon}>
                        <LeafletTooltip permanent direction="top" offset={[0, -22]} opacity={0.95}>
                          <span style={{ fontSize: 11, fontWeight: 600 }}>
                            {assetLabel(activePoint.readerImei, resolveReader(activePoint.readerImei))} · {moment(activePoint.ts).format('HH:mm:ss')}
                            {activePoint.distance !== null && activePoint.distance !== undefined
                              ? ` · ~${activePoint.distance.toFixed(1)} m`
                              : ''}
                          </span>
                        </LeafletTooltip>
                      </Marker>
                    )}
                  </>
                )}
              </LeafletMap>
            </div>
          )}
        </TabPane>

        <TabPane tab={`Exceptions / Needs Review (${exceptions.length})`} key="exceptions">
          {exceptions.length === 0 ? (
            renderEmpty('No exceptions detected. Fetch some reads first.')
          ) : (
            <Table
              rowKey="id"
              size="small"
              columns={exceptionColumns}
              dataSource={exceptions}
              pagination={{ pageSize: 25, showSizeChanger: true }}
              scroll={{ x: 1100 }}
            />
          )}
        </TabPane>
      </Tabs>

      <Drawer
        title={
          drawerRecord ? (
            <span>
              Detection Detail {sensorTag(drawerRecord.sensorType)}
            </span>
          ) : 'Detection Detail'
        }
        width={520}
        visible={drawerOpen}
        onClose={closeDrawer}
        destroyOnClose
      >
        {drawerRecord ? (
          <div>
            <Row gutter={8}>
              <Col span={12}>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>Detected Asset</div>
                {renderAssetCell(drawerRecord.assetIdentifier, resolveTag(drawerRecord.assetIdentifier))}
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>Detected By Reader</div>
                {renderAssetCell(drawerRecord.readerImei, resolveReader(drawerRecord.readerImei))}
              </Col>
            </Row>
            <Row gutter={8} style={{ marginTop: 12 }}>
              <Col span={12}>
                <Statistic title="Last Seen" value={formatTime(drawerRecord.eventTime)} valueStyle={{ fontSize: 14 }} />
              </Col>
              <Col span={12}>
                <Statistic title="Received" value={formatTime(drawerRecord.receivedTime)} valueStyle={{ fontSize: 14 }} />
              </Col>
            </Row>
            <Row gutter={8} style={{ marginTop: 12 }}>
              <Col span={8}>
                <Statistic title="Status" value={drawerRecord.status || '-'} valueStyle={{ fontSize: 14 }} />
              </Col>
              <Col span={8}>
                <Statistic
                  title="RSSI"
                  value={drawerRecord.rssi === null || drawerRecord.rssi === undefined ? '-' : `${drawerRecord.rssi} dBm`}
                  valueStyle={{ fontSize: 14 }}
                />
              </Col>
              <Col span={8}>
                <Statistic title="Zone" value={drawerRecord.zone || '-'} valueStyle={{ fontSize: 14 }} />
              </Col>
            </Row>
            <Row gutter={8} style={{ marginTop: 12 }}>
              <Col span={12}>
                <Statistic
                  title="Latitude"
                  value={drawerRecord.latitude === null ? '-' : drawerRecord.latitude}
                  valueStyle={{ fontSize: 14 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Longitude"
                  value={drawerRecord.longitude === null ? '-' : drawerRecord.longitude}
                  valueStyle={{ fontSize: 14 }}
                />
              </Col>
            </Row>

            {drawerClassification && (
              <Card size="small" style={{ marginTop: 16 }} title="Classification">
                <div style={{ marginBottom: 8 }}>
                  {classificationTag(drawerClassification.classification)}
                </div>
                <div style={{ marginBottom: 12, color: '#555' }}>{drawerClassification.reason}</div>
                <Timeline>
                  {drawerClassification.checks.map((c, idx) => (
                    <Timeline.Item key={idx} color={c.passed ? 'green' : 'gray'}>
                      <Icon type={c.passed ? 'check-circle' : 'close-circle'} style={{ marginRight: 6 }} />
                      {c.label}
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Card>
            )}

            <Card size="small" style={{ marginTop: 16 }} title="Raw JSON">
              <pre style={{ fontSize: 11, maxHeight: 240, overflow: 'auto', background: '#fafafa', padding: 8 }}>
{JSON.stringify(drawerRecord.raw, null, 2)}
              </pre>
            </Card>
          </div>
        ) : null}
      </Drawer>

      <Drawer
        title={`Map data (${panelData.length} ${usingClusters ? 'scans' : usingJourney ? 'journey points' : usingTrack ? 'GPS points' : 'reads'})`}
        placement="right"
        width={580}
        mask={false}
        visible={dataPanelOpen}
        onClose={() => setDataPanelOpen(false)}
      >
        <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
          {usingClusters
            ? 'Each row is one reader scan — expand to see every BLE asset detected at that moment. Highlighted row = current marker; click to seek.'
            : usingJourney
              ? 'Tag journey: dwell rows (where it stayed) and towed-move GPS rows. Highlighted row = current marker; click to seek.'
              : usingTrack
                ? 'Reader GPS track points currently plotted. The highlighted row is the marker’s position now — click any row to seek.'
                : 'Reads currently plotted. The highlighted row is the marker’s position now — click any row to seek.'}
        </div>
        <div ref={dataPanelBodyRef}>
          <Table
            rowKey="key"
            size="small"
            dataSource={panelData}
            pagination={false}
            scroll={{ y: 560, x: 520 }}
            expandedRowRender={(usingClusters || usingTrack || usingJourney) ? (record) => (
              <div style={{ fontSize: 12 }}>
                {(record.detections || []).length === 0 && <span style={{ color: '#999' }}>No assets on board</span>}
                {(record.detections || []).map((d, di) => (
                  <div key={di} style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
                    <span style={{ color: usingTrack ? (d.towed ? '#1890ff' : '#fa8c16') : undefined }}>
                      {usingTrack ? (d.towed ? '➜ ' : '• ') : ''}
                      {assetLabel(d.assetIdentifier, resolveTag(d.assetIdentifier))}
                    </span>
                    <span style={{ color: '#888' }}>
                      {d.rssi !== null && d.rssi !== undefined ? `${d.rssi} dBm` : '-'}
                      {d.status && d.status !== '-' ? ` · ${d.status}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            ) : undefined}
            onRow={(record) => ({
              onClick: () => { setIsPlaying(false); setPlaybackIndex(record._idx); },
              style: record._idx === playbackIndex
                ? { background: '#e6f7ff', cursor: 'pointer' }
                : { cursor: 'pointer' }
            })}
            columns={usingJourney ? [
              { title: '#', key: 'idx', width: 44, render: (_, r) => r._idx + 1 },
              { title: 'Time', key: 'time', render: (_, r) => moment(r.ts).format('YYYY-MM-DD HH:mm:ss') },
              {
                title: 'Type',
                key: 'type',
                width: 90,
                render: (_, r) => (r.dwell ? <Tag color="cyan">dwell</Tag> : <Tag color="blue">move</Tag>)
              },
              { title: 'Asset', key: 'asset', render: (_, r) => assetLabel(r.assetIdentifier, resolveTag(r.assetIdentifier)) },
              {
                title: 'Detail',
                key: 'detail',
                render: (_, r) => (r.dwell
                  ? `${moment(r.dwellStartTs).format('HH:mm')}–${moment(r.dwellEndTs).format('HH:mm')} · ${r.dwellCount || 0} reads`
                  : `with ${assetLabel(r.readerImei, resolveReader(r.readerImei))}${r.speed != null ? ` · ${Number(r.speed).toFixed(0)} km/h` : ''}`)
              }
            ] : usingClusters ? [
              { title: '#', key: 'idx', width: 44, render: (_, r) => r._idx + 1 },
              { title: 'Scan Time', key: 'time', render: (_, r) => moment(r.ts).format('YYYY-MM-DD HH:mm:ss') },
              { title: 'Reader', key: 'reader', render: (_, r) => assetLabel(r.readerImei, resolveReader(r.readerImei)) },
              {
                title: 'Detected',
                key: 'count',
                width: 90,
                render: (_, r) => <Tag color="blue">{r.count}</Tag>
              }
            ] : usingTrack ? [
              { title: '#', key: 'idx', width: 44, render: (_, r) => r._idx + 1 },
              { title: 'Time', key: 'time', render: (_, r) => moment(r.ts).format('YYYY-MM-DD HH:mm:ss') },
              { title: 'Reader', key: 'reader', render: (_, r) => assetLabel(r.readerImei, resolveReader(r.readerImei)) },
              {
                title: 'On board',
                key: 'count',
                width: 130,
                render: (_, r) => (r.count ? (
                  <span>
                    <Tag color="blue">{r.towedCount || 0} towed</Tag>
                    {(r.count - (r.towedCount || 0)) > 0 && <Tag color="orange">{r.count - (r.towedCount || 0)}</Tag>}
                  </span>
                ) : <Tag>empty</Tag>)
              },
              {
                title: 'Speed',
                key: 'speed',
                width: 84,
                render: (_, r) => (r.noGps
                  ? <Tag color="orange">no GPS</Tag>
                  : (r.speed !== null && r.speed !== undefined ? `${Number(r.speed).toFixed(0)} km/h` : '-'))
              }
            ] : [
              { title: '#', key: 'idx', width: 48, render: (_, r) => r._idx + 1 },
              { title: 'Detected At', key: 'time', render: (_, r) => moment(r.ts).format('YYYY-MM-DD HH:mm:ss') },
              { title: 'Detected Asset', key: 'asset', render: (_, r) => assetLabel(r.assetIdentifier, resolveTag(r.assetIdentifier)) },
              { title: 'Reader', key: 'reader', render: (_, r) => assetLabel(r.readerImei, resolveReader(r.readerImei)) },
              { title: 'RSSI', key: 'rssi', width: 70, render: (_, r) => (r.rssi !== null && r.rssi !== undefined ? `${r.rssi} dBm` : '-') },
              { title: '~Dist', key: 'dist', width: 72, render: (_, r) => (r.distance !== null && r.distance !== undefined ? `${r.distance.toFixed(1)} m` : '-') },
              { title: 'Zone', key: 'zone', render: (_, r) => (r.zone && r.zone !== '-' ? r.zone : '-') }
            ]}
          />
        </div>
      </Drawer>
    </div>
  );
}

export default PassiveAssetVisibility;
